import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faSpinner, faGift, faUserCheck, faUserXmark, faRobot } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import CardCheckbox from '@/components/ui/CardCheckbox';
import MarkdownEditor from '@/components/ui/MarkdownEditor.tsx';
import ThumbnailUpload from '@/components/ui/ThumbnailUpload.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import type { Course } from '@/models';
import type { CreateCourseRequest } from '@/modelsAPI';

interface CreateCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCourseCreated: (course: Course) => void;
}

export default function CreateCourseModal({
  isOpen,
  onClose,
  onCourseCreated
}: CreateCourseModalProps) {
  const [formData, setFormData] = useState<CreateCourseRequest>({
    title: '',
    description: '',
    order: 0,
    free: false,
    excludeCommunityMembers: false,
    onlyCommunityMembers: false,
    discordChannelID: '',
    convertkitTagID: null,
    noKnowledge: false
  });
  const [thumbnailData, setThumbnailData] = useState<{ file: File; base64: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // Get max order when modal opens
  useEffect(() => {
    if (isOpen) {
      // Get the highest order value
      APIRoutes.listCourses({
        limit: 1,
        sort: 'order',
        sortDir: 'DESC'
      })
        .then(response => {
          const maxOrder = response.courses.length > 0 ? response.courses[0].order : -1;
          setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
        })
        .catch(err => {
          console.error('Error fetching max order:', err);
          // Default to 0 if fetching fails
          setFormData(prev => ({ ...prev, order: 0 }));
        });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      showErrorToast('Bitte geben Sie einen Titel ein');
      return;
    }


    try {
      setLoading(true);

      const requestData: CreateCourseRequest = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        order: formData.order,
        free: formData.free,
        excludeCommunityMembers: formData.excludeCommunityMembers,
        onlyCommunityMembers: formData.onlyCommunityMembers,
        thumbnailBase64: thumbnailData?.base64 || undefined,
        discordChannelID: formData.discordChannelID?.trim() !== '' ? formData.discordChannelID?.trim() : null,
        convertkitTagID: formData.convertkitTagID,
        noKnowledge: formData.noKnowledge
      };

      const response = await APIRoutes.createCourse(requestData);

      onCourseCreated(response);
      onClose();

      // Reset form
      setFormData({
        title: '',
        description: '',
        order: 0,
        free: false,
        excludeCommunityMembers: false,
        onlyCommunityMembers: false,
        discordChannelID: '',
        convertkitTagID: null,
        noKnowledge: false
      });
      setThumbnailData(null);
    } catch (err) {
      console.error('Error creating course:', err);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFormData({
        title: '',
        description: '',
        order: 0,
        free: false,
        excludeCommunityMembers: false,
        onlyCommunityMembers: false,
        discordChannelID: '',
        convertkitTagID: null,
        noKnowledge: false
      });
      setThumbnailData(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Neuen Kurs erstellen"
      icon={<FontAwesomeIcon icon={faGraduationCap} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Titel *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData((prev: CreateCourseRequest) => ({ ...prev, title: e.target.value }))}
            placeholder="z.B. Trading Grundlagen"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung
          </label>
          <MarkdownEditor
            value={formData.description}
            onChange={(value) => setFormData((prev: CreateCourseRequest) => ({ ...prev, description: value ?? '' }))}
            placeholder="Eine detaillierte Beschreibung des Kurses... (Markdown unterstützt)"
            disabled={loading}
            height={200}
          />
        </div>

        <ThumbnailUpload
          onThumbnailChange={setThumbnailData}
          disabled={loading}
        />

        <div>
          <label htmlFor="discordChannelID" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Discord Channel ID
          </label>
          <input
            type="text"
            id="discordChannelID"
            value={formData.discordChannelID || ''}
            onChange={(e) => setFormData((prev: CreateCourseRequest) => ({ ...prev, discordChannelID: e.target.value }))}
            placeholder="z.B. 1234567890123456789"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Optional: Discord Channel ID für Benachrichtigungen zu diesem Kurs
          </p>
        </div>

        <div>
          <label htmlFor="convertkitTagID" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            ConvertKit Tag ID
          </label>
          <input
            type="number"
            id="convertkitTagID"
            value={formData.convertkitTagID ?? ''}
            onChange={(e) => setFormData((prev: CreateCourseRequest) => ({
              ...prev,
              convertkitTagID: e.target.value !== '' ? parseInt(e.target.value) : null
            }))}
            placeholder="z.B. 1234567"
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Optional: ConvertKit Tag ID für Nutzer-Tagging bei Kurszugang
          </p>
        </div>

        <div>
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reihenfolge
          </label>
          <input
            type="number"
            id="order"
            value={formData.order}
            onChange={(e) => setFormData((prev: CreateCourseRequest) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
            disabled={loading}
            min={0}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
          />
        </div>

        {/* Zugriffseinstellungen */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Zugriffseinstellungen
          </label>
          <div className="space-y-3">
            <CardCheckbox
              checked={formData.free}
              onChange={(checked) => setFormData((prev: CreateCourseRequest) => ({ ...prev, free: checked }))}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faGift} className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />}
              label="Kostenloser Kurs"
              description="Zugänglich für Nutzer ohne aktives Abo. Sobald ein Abo besteht, ist der Kurs nur sichtbar, wenn er im Abo oder manuell freigeschaltet ist."
            />

            <CardCheckbox
              checked={formData.onlyCommunityMembers ?? false}
              onChange={(checked) => setFormData((prev: CreateCourseRequest) => ({ ...prev, onlyCommunityMembers: checked, excludeCommunityMembers: checked ? false : prev.excludeCommunityMembers }))}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faUserCheck} className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />}
              label="Nur für Community-Mitglieder"
              description="Nur Nutzer mit Community-Mitgliedschaft haben Zugang"
            />

            <CardCheckbox
              checked={formData.excludeCommunityMembers ?? false}
              onChange={(checked) => setFormData((prev: CreateCourseRequest) => ({ ...prev, excludeCommunityMembers: checked, onlyCommunityMembers: checked ? false : prev.onlyCommunityMembers }))}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faUserXmark} className="w-4 h-4 text-orange-600 dark:text-orange-400 mr-2" />}
              label="Community-Mitglieder ausschließen"
              description="Community-Mitglieder haben keinen Zugang zu diesem Kurs"
            />
          </div>
        </div>

        {/* KI-Einstellungen */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            KI-Einstellungen
          </label>
          <div className="space-y-3">
            <CardCheckbox
              checked={formData.noKnowledge ?? false}
              onChange={(checked) => setFormData((prev: CreateCourseRequest) => ({ ...prev, noKnowledge: checked }))}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faRobot} className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />}
              label="Keine KI-Analyse"
              description="Videos in diesem Kurs werden nicht analysiert und für den KI Chat verwendet"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Erstelle...
              </>
            ) : (
              'Kurs erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
